import { isRecord, type Middleware } from '@hello-aria/api-client'
import { createMakaluClient } from '@hello-aria/makalu-api-client'
import type { MakaluAppPaths } from '../models/makaluApiOverrides'

// The proxy route under this path forwards to makalu-api with the access token of the session.
const MAKALU_PROXY_PATH = '/api/proxy'

const EXPIRED_SESSION_CODES: unknown[] = ['AUTH_ERROR', 'ACCESS_TOKEN_ERROR']

const LOGOUT_PATH = '/auth/logout'

const readJson = (response: Response): Promise<unknown> =>
    response
        .clone()
        .json()
        .catch(() => undefined)

/*
 * An expired session sends the browser to the logout page, as `requestService` does. The request
 * then never settles, so no error shows while the page leaves.
 */
const logoutOnExpiredSession: Middleware = {
    async onResponse({ response }) {
        // Server code has no page to leave, so the 401 throws as any failure does.
        if (response.status !== 401 || typeof window === 'undefined') return undefined

        const body = await readJson(response)
        if (!isRecord(body) || !EXPIRED_SESSION_CODES.includes(body.code)) return undefined

        location.href = LOGOUT_PATH
        return new Promise<Response>(() => {})
    }
}

export const makaluApi = createMakaluClient<MakaluAppPaths>({
    baseUrl: MAKALU_PROXY_PATH,
    middleware: [logoutOnExpiredSession]
})

/**
 * A client for server code, which calls makalu-api directly with an access token it already holds.
 * A route handler has no page to leave, so an expired session throws an `HttpError`.
 */
export const createServerMakaluApi = (accessToken: string | undefined) => {
    return createMakaluClient<MakaluAppPaths>({
        baseUrl: process.env.NEXT_PUBLIC_BASE_API_URL ?? '',
        getToken: async () => accessToken
    })
}
